import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Download, Upload } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import {
  DashboardPageFormActions,
  DashboardPageFormBody,
  DashboardPageFormShell,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  Checkbox,
  FilePicker,
  Form,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
} from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { downloadBlob } from '../../../../../lib/downloadBlob';
import { dashboardT } from '../../../../../lib/messages';
import { useInitializeFormFromFreshQuery } from '../../../../../lib/useInitializeFormFromFreshQuery';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { canExportLists, canImportLists } from '../../access';
import { useListsMutations, useSchainListQuery } from '../../hooks';
import { SchainType } from '../../model';
import {
  schainExportFilename,
  defaultSchainFormValue,
  schainFormSchema,
  schainListToFormValue,
  serializeSchainFormValue,
  type SchainFormValue,
} from '../../schainForm';

export function SchainListEditorPage({ listId }: { listId?: string }) {
  const navigate = useNavigate();
  const { returnTo } = useReturnNavigation({ to: '/supply/lists/schain' });
  const { can } = useShigoPermissions();
  const canImport = canImportLists(can);
  const canExport = canExportLists(can);
  const query = useSchainListQuery(listId);
  const mutations = useListsMutations();
  const [file, setFile] = React.useState<File | null>(null);
  const pending =
    mutations.createSchain.isPending ||
    mutations.updateSchain.isPending ||
    mutations.importSchain.isPending;
  const initialValue = React.useMemo(
    () => (query.data ? schainListToFormValue(query.data) : defaultSchainFormValue),
    [query.data],
  );
  const form = useForm<SchainFormValue>({
    resolver: zodResolver(schainFormSchema),
    defaultValues: initialValue,
  });
  const freshDetail = !listId || query.isFetchedAfterMount;
  useInitializeFormFromFreshQuery(form, initialValue, freshDetail, listId);
  const loadingDetail = Boolean(listId) && !freshDetail && !query.isError;

  async function save(value: SchainFormValue) {
    try {
      if (!listId) {
        const created = await mutations.createSchain.mutateAsync(serializeSchainFormValue(value));
        toast.success(dashboardT('lists.schain.created'));
        await navigate({
          to: '/supply/lists/schain/edit/$listId',
          params: { listId: created.id },
          replace: true,
        });
        return;
      }
      await mutations.updateSchain.mutateAsync({
        id: listId,
        payload: serializeSchainFormValue(value, query.data),
      });
      toast.success(dashboardT('lists.schain.updated'));
      await returnTo({ replace: true });
    } catch (error) {
      showRequestError(error, dashboardT('lists.schain.saveError'));
    }
  }

  async function importCsv() {
    if (!listId || !file) return;
    try {
      await mutations.importSchain.mutateAsync({ id: listId, file });
      setFile(null);
      toast.success(dashboardT('lists.schain.imported'));
      await query.refetch();
    } catch (error) {
      showRequestError(error, dashboardT('lists.schain.importError'));
    }
  }

  async function exportCsv() {
    if (!listId) return;
    try {
      const blob = await mutations.exportSchain.mutateAsync(listId);
      downloadBlob(blob, schainExportFilename(form.getValues('name')));
    } catch (error) {
      showRequestError(error, dashboardT('lists.schain.exportError'));
    }
  }

  const title = dashboardT(listId ? 'lists.schain.editTitle' : 'lists.schain.createTitle');
  return (
    <Form {...form}>
      <DashboardPageFormShell
        title={title}
        backAction={
          pending ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled
              aria-label={dashboardT('lists.back')}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
          ) : (
            <ReturnBackLink
              fallback={{ to: '/supply/lists/schain' }}
              label={dashboardT('lists.back')}
            />
          )
        }
      >
        {loadingDetail ? (
          <DashboardPageFormBody width="lg">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </DashboardPageFormBody>
        ) : (
          <form
            onSubmit={(event) => {
              void form.handleSubmit(save)(event);
            }}
          >
            <DashboardPageFormBody width="lg">
              {query.isError ? (
                <p role="alert" className="text-destructive text-sm">
                  {dashboardT('lists.schain.detailError')}
                </p>
              ) : null}
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="schain-name">
                    {dashboardT('lists.name')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="schain-name"
                    {...form.register('name')}
                    aria-invalid={Boolean(form.formState.errors.name)}
                  />
                  {form.formState.errors.name?.message ? (
                    <p role="alert" className="text-destructive text-sm">
                      {form.formState.errors.name.message}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="schain-type">
                    {dashboardT('lists.schain.type')} <span className="text-destructive">*</span>
                  </Label>
                  <Controller
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger id="schain-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SchainType.Bundle}>
                            {dashboardT('lists.schain.bundle')}
                          </SelectItem>
                          <SelectItem value={SchainType.Domain}>
                            {dashboardT('lists.schain.domain')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>
              <div className="border-input flex items-start gap-3 rounded-md border p-4">
                <Controller
                  control={form.control}
                  name="enableRotationForDuplicateBundles"
                  render={({ field }) => (
                    <Checkbox
                      id="schain-rotation"
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked === true);
                      }}
                    />
                  )}
                />
                <span>
                  <Label htmlFor="schain-rotation" className="block text-sm font-medium">
                    {dashboardT('lists.schain.rotation')}
                  </Label>
                  <span className="text-muted-foreground mt-1 block text-sm">
                    {dashboardT('lists.schain.rotationHelp')}
                  </span>
                </span>
              </div>
              <section className="border-input overflow-hidden rounded-md border">
                <header className="bg-muted/40 border-input flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
                  <div>
                    <h2 className="text-sm font-semibold">{dashboardT('lists.schain.entries')}</h2>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      {dashboardT('lists.schain.entryCount', {
                        count: query.data?.schainBundleList.length ?? 0,
                      })}
                    </p>
                  </div>
                  {canExport ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!listId || mutations.exportSchain.isPending}
                      onClick={() => void exportCsv()}
                    >
                      <Download className="h-4 w-4" aria-hidden="true" />
                      {dashboardT('lists.schain.export')}
                    </Button>
                  ) : null}
                </header>
                <div className="space-y-2 p-4">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <FilePicker
                      className="min-w-0 flex-1"
                      label={dashboardT('lists.schain.csvFile')}
                      accept=".csv,text/csv"
                      value={file}
                      onFileChange={setFile}
                      disabled={!listId || pending || !canImport}
                      helperText={
                        !listId
                          ? dashboardT('lists.schain.saveFirst')
                          : dashboardT('lists.schain.chooseCsv')
                      }
                    />
                    {canImport ? (
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!listId || !file || pending}
                        onClick={() => void importCsv()}
                      >
                        <Upload className="h-4 w-4" aria-hidden="true" />
                        {dashboardT('lists.schain.import')}
                      </Button>
                    ) : null}
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {dashboardT('lists.schain.csvColumns')}
                  </p>
                </div>
              </section>
            </DashboardPageFormBody>
            <DashboardPageFormActions>
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                onClick={() => void returnTo()}
              >
                {dashboardT('lists.cancel')}
              </Button>
              <Button type="submit" disabled={pending || query.isError}>
                {pending ? dashboardT('lists.saving') : dashboardT('lists.save')}
              </Button>
            </DashboardPageFormActions>
          </form>
        )}
      </DashboardPageFormShell>
    </Form>
  );
}
